import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { cur, fmtMoney, isAusDesk, rl, ageingBasePath, setCollectionsRegion } from "@/lib/regionFmt";
import { ukDate } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useRegion } from "@/contexts/RegionContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  AlarmClock, Upload, Search, Loader2, ChevronRight, Phone, Mail,
  MessageSquare, FileText, ArrowRightCircle, User, X, Send, ExternalLink,
  CalendarClock, CheckCircle2, AlertOctagon, Ban, Flame, FileSpreadsheet, TrendingUp,
  ChevronDown, ChevronUp, History,
} from "lucide-react";
import {
  getAgeingHandlers, getAgeingStages, listAgeingCases,
  updateAgeingCase, addAgeingNote, deleteAgeingNote, uploadArrearsReport,
  bulkAssignAgeingCases, getAgeingLoanDiskSnapshot,
  bulkSetStage,
  getAgeingAircallCalls, getAgeingMondayCard,
  getAgeingAircallSms,
  getAgeingM365Emails, sendAgeingM365Email,
  syncAgeingM365History, getAgeingM365EmailFull, replyAgeingM365Email,
  m365SearchMailbox,
  createAgeingP2P, updateAgeingP2P, deleteAgeingP2P,
  bulkAgeingContactability,
  listArrearsSnapshots, getArrearsSnapshotDetail,
  getAgeingReconcile, applyAgeingReconcile,
} from "@/lib/api";
import { handlerIdFor, rosterFrom } from "@/lib/handlerRoster";
import { WorklistReconcileBanner } from "./WorklistReconcileBanner";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AirtableFilter, FilterChips, applyAirtableFilters } from "./AirtableFilter";
import ArrearsHistoryView from "./ArrearsHistoryView";
import { sanitizedHtmlProps } from '@/lib/sanitizeEmailHtml';
import { StatusTagChips, useAccountStatuses } from "@/components/collections/StatusTags";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

// Currency formatter (matches the bad-debt pages)
const fmtGBP = (v) => fmtMoney(v, 0);
const fmtGBPdp = (v) => fmtMoney(v || 0, 2);

// Stage tone → tailwind class (2-stage model + legacy fallbacks)
const STAGE_TONES = {
  open_loan:       "bg-amber-100 text-amber-900 border-amber-300",
  legal:           "bg-red-100 text-red-900 border-red-300",
  // legacy values (still in DB until next read normalises them)
  arrears:         "bg-amber-100 text-amber-900 border-amber-300",
  capital_at_risk: "bg-orange-100 text-orange-900 border-orange-300",
  bad_debt:        "bg-red-100 text-red-900 border-red-300",
  restructured:    "bg-blue-100 text-blue-900 border-blue-300",
  written_off:     "bg-slate-200 text-slate-700 border-slate-400",
};

// Rich gradient tones for the KPI tile backgrounds (Monday-style)
const STAGE_KPI_TONES = {
  open_loan:       { grad: "from-amber-50 to-amber-100",   ring: "ring-amber-300",   accent: "text-amber-900",   dot: "bg-amber-500" },
  legal:           { grad: "from-red-50 to-red-100",       ring: "ring-red-300",     accent: "text-red-900",     dot: "bg-red-500" },
  // CAR isn't a stage anymore but the KPI tile still renders a CAR card
  capital_at_risk: { grad: "from-orange-50 to-orange-100", ring: "ring-orange-300",  accent: "text-orange-900",  dot: "bg-orange-500" },
};

// DPD buckets — ordered, with tone + label
const DPD_BUCKETS = [
  { key: "0_7",     label: "1-7 days",   tone: "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100" },
  { key: "8_30",    label: "8-30 days",  tone: "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100" },
  { key: "31_60",   label: "31-60 days", tone: "bg-orange-50 text-orange-800 border-orange-300 hover:bg-orange-100" },
  { key: "61_90",   label: "61-90 days", tone: "bg-rose-50 text-rose-800 border-rose-300 hover:bg-rose-100" },
  { key: "90_plus", label: "90+ days",   tone: "bg-red-50 text-red-900 border-red-400 hover:bg-red-100" },
];

// Quick DPD chip on rows
const dpdRowTone = (dpd) => {
  if (!dpd && dpd !== 0) return "bg-slate-100 text-slate-600";
  if (dpd <= 7)   return "bg-emerald-100 text-emerald-800";
  if (dpd <= 30)  return "bg-amber-100 text-amber-800";
  if (dpd <= 60)  return "bg-orange-100 text-orange-800";
  if (dpd <= 90)  return "bg-rose-100 text-rose-800";
  return "bg-red-100 text-red-900";
};

// Note type → icon + tone
const NOTE_TYPE_META = {
  call:           { icon: Phone,         tone: "text-emerald-700", label: "Call" },
  email_in:       { icon: Mail,          tone: "text-blue-700",    label: "Email in" },
  email_out:      { icon: Mail,          tone: "text-purple-700",  label: "Email out" },
  sms:            { icon: MessageSquare, tone: "text-cyan-700",    label: "SMS" },
  note:           { icon: FileText,      tone: "text-slate-700",   label: "Note" },
  stage_change:   { icon: ArrowRightCircle, tone: "text-amber-700", label: "Stage change" },
  handler_change: { icon: User,          tone: "text-indigo-700",  label: "Handler change" },
};

// Emma #1 — filters survive navigating into a case and back (per browser tab).
const AGEING_FILTERS_SS_KEY = "ageing-filters-v1";
const loadAgeingSaved = () => {
  try { return JSON.parse(sessionStorage.getItem(AGEING_FILTERS_SS_KEY) || "null") || {}; } catch { return {}; }
};

export default function LoanAgeing() {
  const navigate = useNavigate();
  const { currentRegion } = useRegion();
  setCollectionsRegion(currentRegion);   // module flag → api interceptor + money formatting
  const isAus = currentRegion === "AUS";
  const [cases, setCases] = useState([]);
  const [offListMatches, setOffListMatches] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Contactability traffic-light dots — bulk-loaded once for all visible cases.
  // Map of case_id → {status, score, attempts, response_rate}.
  const [contactability, setContactability] = useState({});
  const [byStage, setByStage] = useState({});
  const [byHandler, setByHandler] = useState({});
  const [byDpdBucket, setByDpdBucket] = useState({});
  const [arrearsTotal, setArrearsTotal] = useState(0);
  const [balanceTotal, setBalanceTotal] = useState(0);
  const [capitalAtRiskCount, setCapitalAtRiskCount] = useState(0);
  const [bsProtectedCount, setBsProtectedCount] = useState(0);
  const [latestSnapshot, setLatestSnapshot] = useState(null);
  const [handlers, setHandlers] = useState([]);
  const [roster, setRoster] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(() => loadAgeingSaved().filter || { search: "", handler: "", stage: "", dpd_bucket: "", bs_protected: "" });
  const accountStatuses = useAccountStatuses();   // Emma #14
  const [airtableFilters, setAirtableFilters] = useState(() => loadAgeingSaved().airtableFilters || []);
  const [sort, setSort] = useState(() => loadAgeingSaved().sort || { by: "days_past_due", dir: "desc" });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const openCase = useCallback((c) => navigate(`${ageingBasePath()}/${c.case_id}`), [navigate]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStagePending, setBulkStagePending] = useState(null);  // {stage, count}
  // History mode — pick a stored snapshot to view the book as of that date
  const [historyList, setHistoryList] = useState([]);
  const [historySnap, setHistorySnap] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Item 4: KPI + DPD chip strip is collapsible — persisted to localStorage
  // so ops who prefer the table-only view keep their preference.
  const [chipsCollapsed, setChipsCollapsed] = useState(() => {
    try { return localStorage.getItem("ageing-chips-collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("ageing-chips-collapsed", chipsCollapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [chipsCollapsed]);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const r = await listAgeingCases({ ...filter, sort_by: sort.by, sort_dir: sort.dir });
      setCases(r.cases || []);
      setOffListMatches(r.off_list_matches || []);
      setTotalCount(r.total_count || 0);
      setByStage(r.by_stage || {});
      setByHandler(r.by_handler || {});
      setByDpdBucket(r.by_dpd_bucket || {});
      setArrearsTotal(r.arrears_total || 0);
      setBalanceTotal(r.balance_total || 0);
      setCapitalAtRiskCount(r.capital_at_risk_count || 0);
      setBsProtectedCount(r.bs_protected_count || 0);
      setLatestSnapshot(r.latest_snapshot || null);

      // Bulk contactability — fires in parallel after cases load. Failure is
      // non-fatal: dots simply don't render.
      bulkAgeingContactability({ limit: 250 })
        .then(rr => {
          const m = {};
          for (const it of (rr.items || [])) m[it.case_id] = it;
          setContactability(m);
        })
        .catch(() => {});
    } catch (e) {
      toast.error("Failed to load cases: " + errorDetailMessage(e, e.message));
    } finally {
      setLoading(false);
    }
  }, [filter, sort]);

  useEffect(() => {
    (async () => {
      try {
        const [h, s] = await Promise.all([getAgeingHandlers("collections"), getAgeingStages()]);
        setHandlers(h.handlers || []);
        setRoster(rosterFrom(h));
        setStages(s.stages || []);
      } catch (e) {
        toast.error("Failed to load reference data: " + errorDetailMessage(e, e.message));
      }
      // Snapshot history for the date selector — non-fatal if it fails.
      try {
        const snaps = await listArrearsSnapshots(120);
        setHistoryList(snaps.snapshots || []);
      } catch { /* selector just stays empty */ }
    })();
  }, []);

  const openHistorySnapshot = useCallback(async (snapshotId) => {
    if (snapshotId === "live") { setHistorySnap(null); return; }
    setHistoryLoading(true);
    try {
      const doc = await getArrearsSnapshotDetail(snapshotId);
      setHistorySnap(doc);
    } catch (e) {
      toast.error("Failed to load snapshot: " + errorDetailMessage(e, e.message));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const stageMeta = useMemo(() => {
    const m = {};
    stages.forEach(s => { m[s.key] = s; });
    return m;
  }, [stages]);

  // Emma #1 — persist the active filters/sort for this browser tab
  useEffect(() => {
    try { sessionStorage.setItem(AGEING_FILTERS_SS_KEY, JSON.stringify({ filter, airtableFilters, sort })); } catch { /* ignore */ }
  }, [filter, airtableFilters, sort]);

  // Reset selection whenever the visible result set changes
  useEffect(() => { setSelectedIds(new Set()); }, [filter, sort]);

  // Airtable-style filter field metadata — drives the field/operator UI
  const filterFields = useMemo(() => [
    { key: "customer_name",         label: "Customer name",   type: "text" },
    { key: "loan_application_id",   label: "SF#",             type: "text" },
    { key: "borrower_director_name", label: "Director",       type: "text" },
    { key: "days_past_due",         label: "Days past due",   type: "number" },
    { key: "arrears_amount",        label: `Arrears ${cur()}`,       type: "number" },
    { key: "balance_amount",        label: `Balance ${cur()}`,       type: "number" },
    { key: "principal_amount",      label: `Principal ${cur()}`,     type: "number" },
    { key: "stage",                 label: "Stage",           type: "select",
      options: stages.map(s => ({ value: s.key, label: s.label })) },
    { key: "handler",               label: "Handler",         type: "select",
      options: [{ value: "__empty__", label: "Unassigned" }, ...handlers.filter(Boolean).map(h => ({ value: h, label: h }))] },
    { key: "loandisk_status_text",  label: "LoanDisk status", type: "select",
      options: [
        { value: "Current",          label: "Current" },
        { value: "Missed Repayment", label: "Missed Repayment" },
        { value: "Arrears",          label: "Arrears" },
        { value: "Past Maturity",    label: "Past Maturity" },
        { value: "Legal",            label: "Legal" },
        { value: "Restructured",     label: "Restructured" },
      ] },
    { key: "loan_officer",          label: "Loan officer",    type: "text" },
    { key: "loan_product",          label: "Loan product",    type: "text" },
    { key: "released_date",         label: "Released date",   type: "date" },
    { key: "last_payment_date",     label: "Last payment date", type: "date" },
  ], [handlers, stages]);

  const visibleCases = useMemo(
    () => applyAirtableFilters(cases, airtableFilters, filterFields),
    [cases, airtableFilters, filterFields],
  );

  const visibleIds = useMemo(() => visibleCases.map(c => c.case_id), [visibleCases]);

  // Emma #3 — multi-handler filter (comma-joined in filter.handler)
  const selectedHandlers = useMemo(
    () => (filter.handler ? filter.handler.split(",").filter(Boolean) : []),
    [filter.handler],
  );
  const toggleHandler = (h) => setFilter(f => {
    const cur = f.handler ? f.handler.split(",").filter(Boolean) : [];
    const next = cur.includes(h) ? cur.filter(x => x !== h) : [...cur, h];
    return { ...f, handler: next.join(",") };
  });
  const handlerLabel = selectedHandlers.length === 0 ? "All handlers"
    : selectedHandlers.length === 1 ? selectedHandlers[0]
      : `${selectedHandlers.length} handlers`;

  // Emma #3/#10 — export the FILTERED view so a handler can work from Excel
  const exportCsv = () => {
    const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const head = ["Customer", "SF#", "Director", "DPD", `Arrears ${cur()}`, `Principal Out ${cur()}`, `Total Out ${cur()}`,
                  "Stage", "Handler", "Loan officer", "LoanDisk status", "BS protected", "Capital at risk"];
    const rows = visibleCases.map(c => [
      c.customer_name || "", c.loan_application_id || "", c.borrower_director_name || "",
      c.days_past_due ?? "", c.arrears_amount ?? "",
      c.total_principal_outstanding ?? c.principal_outstanding ?? "", c.balance_amount ?? "",
      stageMeta[c.stage]?.label || c.stage || "", c.handler || "Unassigned",
      c.loan_officer || "", c.loandisk_status_text || "",
      c.breathing_space?.active ? "yes" : "", c.capital_at_risk ? "yes" : "",
    ]);
    const csv = [head, ...rows].map(r => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `loan-arrears-ageing-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${rows.length} case${rows.length === 1 ? "" : "s"} to CSV`);
  };
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleRow = (caseId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId); else next.add(caseId);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const everySelected = visibleIds.every(id => next.has(id));
      if (everySelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleBulkAssign = async (handlerName) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const label = handlerName || "Unassigned";
    setBulkBusy(true);
    try {
      const r = await bulkAssignAgeingCases({
        case_ids: ids,
        handler: handlerName,
        handler_user_id: handlerIdFor(roster, handlerName),
        actor: "Ops",
      });
      toast.success(`Bulk assign: ${r.changed} case${r.changed === 1 ? "" : "s"} → ${label}${r.unchanged ? ` (${r.unchanged} already on ${label})` : ""}`);
      setSelectedIds(new Set());
      await reload();
    } catch (e) {
      toast.error("Bulk assign failed: " + errorDetailMessage(e, e.message));
    } finally {
      setBulkBusy(false);
    }
  };

  const requestBulkStage = (stage) => {
    if (!stage || selectedIds.size === 0) return;
    setBulkStagePending({ stage, count: selectedIds.size });
  };

  const confirmBulkStage = async () => {
    if (!bulkStagePending) return;
    const { stage } = bulkStagePending;
    const ids = Array.from(selectedIds);
    setBulkBusy(true);
    try {
      const r = await bulkSetStage(ids, stage, { actor: "Ops" });
      const ldPushed = (r.results || []).filter(x => x.ld_push?.ok).length;
      const ldFailed = (r.results || []).filter(x => x.ld_push && x.ld_push.ok === false).length;
      let msg = `${r.succeeded}/${r.touched} cases → ${stage}`;
      if (ldPushed) msg += ` · ${ldPushed} pushed to LoanDisk`;
      if (ldFailed) msg += ` · ${ldFailed} LoanDisk pushes FAILED`;
      if (ldFailed > 0) toast.error(msg);
      else toast.success(msg);
      setSelectedIds(new Set());
      setBulkStagePending(null);
      await reload();
    } catch (e) {
      toast.error("Bulk stage update failed: " + errorDetailMessage(e, e.message));
    } finally {
      setBulkBusy(false);
    }
  };

  // Format snapshot uploaded_at to relative human-readable
  const snapshotMeta = useMemo(() => {
    if (!latestSnapshot) return null;
    const uploaded = latestSnapshot.uploaded_at ? new Date(latestSnapshot.uploaded_at) : null;
    let rel = "";
    if (uploaded) {
      const mins = Math.floor((Date.now() - uploaded.getTime()) / 60000);
      if (mins < 1) rel = "just now";
      else if (mins < 60) rel = `${mins}m ago`;
      else if (mins < 60 * 24) rel = `${Math.floor(mins / 60)}h ago`;
      else rel = `${Math.floor(mins / (60 * 24))}d ago`;
    }
    return { uploaded, rel };
  }, [latestSnapshot]);

  return (
    <div className="p-4 space-y-3 max-w-[1600px] mx-auto" data-testid="loan-ageing-page">
      {/* Compact header — title, report meta + KPIs inline, upload top-right */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <AlarmClock className="w-5 h-5 text-amber-600 shrink-0" />
          <h1 className="text-base font-semibold whitespace-nowrap">Loan Arrears Ageing</h1>
          {latestSnapshot ? (
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground min-w-0" data-testid="ageing-snapshot-hero">
              <span className="text-slate-400">·</span>
              <FileSpreadsheet className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
              <span className="truncate max-w-[260px]" title={latestSnapshot.filename}>
                {latestSnapshot.filename || "LoanDisk Arrears Ageing report"}
              </span>
              {snapshotMeta?.uploaded && (
                <span className="whitespace-nowrap">· <span className="text-indigo-600">{snapshotMeta.rel}</span></span>
              )}
              <span className="text-slate-400">·</span>
              <span className="whitespace-nowrap">
                <span className="font-semibold text-indigo-900 tabular-nums" data-testid="ageing-snapshot-cases">{latestSnapshot.row_count ?? totalCount}</span>
                <span className="text-slate-500 ml-1">cases</span>
              </span>
              <span className="whitespace-nowrap">
                <span className="font-semibold text-rose-700 tabular-nums" data-testid="ageing-snapshot-arrears">{fmtGBP(latestSnapshot.arrears_amount ?? arrearsTotal)}</span>
                <span className="text-slate-500 ml-1">arrears</span>
              </span>
              <span className="whitespace-nowrap">
                <span className="font-semibold text-slate-800 tabular-nums" data-testid="ageing-snapshot-balance">{fmtGBP(latestSnapshot.balance_amount ?? balanceTotal)}</span>
                <span className="text-slate-500 ml-1">balance</span>
              </span>
            </div>
          ) : (
            <span className="text-[11px] text-amber-700 italic">
              · No report uploaded yet — upload to seed the workflow.
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* History date selector — view the book as of any stored report */}
          {historyList.length > 0 && (
            <Select
              value={historySnap?.snapshot_id || "live"}
              onValueChange={openHistorySnapshot}
            >
              <SelectTrigger className="h-8 w-[210px] text-xs" data-testid="ageing-history-select">
                <History className="w-3.5 h-3.5 mr-1 text-indigo-600 shrink-0" />
                <SelectValue placeholder="Today (live)" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="live">Today (live)</SelectItem>
                {historyList.map(s => (
                  <SelectItem key={s.snapshot_id} value={s.snapshot_id}>
                    {formatSnapshotOption(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {historyLoading && <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />}
          <Button onClick={() => setUploadOpen(true)} data-testid="ageing-upload-btn" size="sm" className="h-8 shrink-0 text-xs">
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Upload report
          </Button>
        </div>
      </div>

      {historySnap ? (
        <ArrearsHistoryView snapshot={historySnap} onBack={() => setHistorySnap(null)} />
      ) : (
      <>
      <WorklistReconcileBanner
        reportRows={latestSnapshot?.row_count}
        onList={totalCount}
        fetchReconcile={getAgeingReconcile}
        applyReconcile={() => applyAgeingReconcile("Ops")}
        onApplied={reload}
        snapshotKey={latestSnapshot?.snapshot_id}
      />
      {/* Compact KPI + DPD row — stage pills | DPD chips, both filterable.
          Item 4: Collapsible (state persisted to localStorage).
          Item 5: Each active chip shows a small × to clear individually. */}
      <div className="flex items-start gap-2 flex-wrap text-xs">
        <button
          onClick={() => setChipsCollapsed(v => !v)}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-md border bg-white text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors shrink-0"
          title={chipsCollapsed ? "Show stage & DPD filters" : "Hide stage & DPD filters"}
          data-testid="ageing-chips-collapse-toggle"
        >
          {chipsCollapsed
            ? <ChevronDown className="w-3.5 h-3.5" />
            : <ChevronUp className="w-3.5 h-3.5" />}
          <span className="text-[10px] uppercase tracking-wider font-semibold">
            Quick filters
          </span>
        </button>
      {!chipsCollapsed && (
      <div className="flex items-center gap-2 flex-wrap flex-1 text-xs">
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="ageing-kpi-row">
          {stages.map(s => {
            const count = byStage[s.key] || 0;
            const isFilter = filter.stage === s.key;
            const tones = STAGE_KPI_TONES[s.key] || STAGE_KPI_TONES.open_loan;
            return (
              <button
                key={s.key}
                onClick={() => setFilter(f => ({ ...f, stage: isFilter ? "" : s.key }))}
                className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-md border transition-all ${
                  isFilter
                    ? `${tones.ring} ring-2 ring-offset-1 border-transparent bg-white shadow-sm`
                    : `bg-white border-slate-200 hover:border-slate-300`
                }`}
                data-testid={`ageing-kpi-${s.key}`}
                title={isFilter ? `Active — click × to clear` : `Filter by ${s.label}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${tones.dot}`} />
                <span className={`text-[10px] font-semibold uppercase tracking-wide ${tones.accent}`}>{s.label}</span>
                <span className={`text-xs font-bold tabular-nums ${tones.accent}`}>{count}</span>
                {isFilter && (
                  <X
                    className={`w-3 h-3 ml-0.5 ${tones.accent} opacity-70 hover:opacity-100`}
                    aria-label={`Clear ${s.label} filter`}
                  />
                )}
              </button>
            );
          })}

          {/* CAR pill — portal-only flag, layered on top of any stage */}
          {capitalAtRiskCount > 0 && (
            <button
              onClick={() => setFilter(f => ({ ...f, capital_at_risk: f.capital_at_risk ? "" : "1" }))}
              className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-md border transition-all ${
                filter.capital_at_risk
                  ? "ring-2 ring-orange-300 ring-offset-1 border-transparent bg-white shadow-sm"
                  : "bg-white border-slate-200 hover:border-slate-300"
              }`}
              data-testid="ageing-kpi-capital_at_risk"
              title={filter.capital_at_risk ? "Active — click × to clear" : "Filter by Capital at Risk (portal-only management flag)"}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-orange-900">Capital at Risk</span>
              <span className="text-xs font-bold tabular-nums text-orange-900">{capitalAtRiskCount}</span>
              {filter.capital_at_risk && (
                <X className="w-3 h-3 ml-0.5 text-orange-900 opacity-70 hover:opacity-100" aria-label="Clear CAR filter" />
              )}
            </button>
          )}
          {/* BS Protected pill — active Breathing Space cases, contact must pause */}
          {bsProtectedCount > 0 && (
            <button
              onClick={() => setFilter(f => ({ ...f, bs_protected: f.bs_protected ? "" : "1" }))}
              className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-md border transition-all ${
                filter.bs_protected
                  ? "ring-2 ring-purple-300 ring-offset-1 border-transparent bg-white shadow-sm"
                  : "bg-white border-slate-200 hover:border-slate-300"
              }`}
              data-testid="ageing-kpi-bs_protected"
              title={filter.bs_protected ? "Active — click × to clear" : `Filter by BS Protected (active ${rl("hold")} — do not contact)`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-purple-600" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-900">BS Protected</span>
              <span className="text-xs font-bold tabular-nums text-purple-900">{bsProtectedCount}</span>
              {filter.bs_protected && (
                <X className="w-3 h-3 ml-0.5 text-purple-900 opacity-70 hover:opacity-100" aria-label="Clear BS filter" />
              )}
            </button>
          )}
        </div>
        <span className="text-slate-300 mx-1">|</span>
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground inline-flex items-center gap-1">
            <Flame className="w-3 h-3 text-amber-600" /> DPD
          </span>
          {DPD_BUCKETS.map(b => {
            const cnt = byDpdBucket[b.key] || 0;
            const active = filter.dpd_bucket === b.key;
            return (
              <button
                key={b.key}
                onClick={() => setFilter(f => ({ ...f, dpd_bucket: active ? "" : b.key }))}
                disabled={cnt === 0 && !active}
                className={`px-2 h-6 inline-flex items-center gap-1 rounded-md text-[11px] font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  active ? "bg-slate-900 text-white border-slate-900" : b.tone
                }`}
                data-testid={`ageing-dpd-${b.key}`}
                title={active ? "Active — click × to clear" : `Filter by ${b.label}`}
              >
                {b.label} <span className="tabular-nums opacity-80">{cnt}</span>
                {active && <X className="w-3 h-3 ml-0.5 opacity-80" aria-label={`Clear ${b.label} filter`} />}
              </button>
            );
          })}
          {filter.dpd_bucket && (
            <button
              onClick={() => setFilter(f => ({ ...f, dpd_bucket: "" }))}
              className="text-[10px] text-muted-foreground hover:text-rose-700 underline underline-offset-2 px-1"
              data-testid="ageing-dpd-clear-all"
            >
              clear DPD
            </button>
          )}
        </div>
      </div>
      )}
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
          <Input
            placeholder="Search customer name or SF#…"
            value={filter.search}
            onChange={(e) => setFilter(f => ({ ...f, search: e.target.value }))}
            className="pl-9"
            data-testid="ageing-search-input"
          />
        </div>
        <Button
          variant={filter.handler === "Unassigned" ? "default" : "outline"}
          size="sm"
          className="h-9"
          onClick={() => setFilter(f => ({ ...f, handler: f.handler === "Unassigned" ? "" : "Unassigned" }))}
          data-testid="ageing-unassigned-chip"
        >
          Unassigned ({byHandler["Unassigned"] || 0})
        </Button>
        <AirtableFilter
          fields={filterFields}
          value={airtableFilters}
          onChange={setAirtableFilters}
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-9 w-[180px] justify-between font-normal" data-testid="ageing-handler-filter">
              <span className="truncate">{handlerLabel}</span>
              <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-60 p-2" align="start">
            <label className="flex items-center gap-2 text-xs px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer font-medium" data-testid="ageing-handler-option-all">
              <Checkbox checked={selectedHandlers.length === 0}
                onCheckedChange={() => setFilter(f => ({ ...f, handler: "" }))} />
              <span className="flex-1">All handlers</span>
            </label>
            <div className="border-t my-1" />
            <div className="space-y-0.5 max-h-72 overflow-y-auto">
              {[...new Set(["Unassigned", ...handlers,
                ...Object.keys(byHandler).filter(h => h && h !== "Unassigned")])].map(h => (
                <label key={h} className="flex items-center gap-2 text-xs px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer" data-testid={`ageing-handler-option-${h}`}>
                  <Checkbox checked={selectedHandlers.includes(h)} onCheckedChange={() => toggleHandler(h)} />
                  <span className="flex-1 truncate">{h}</span>
                  <span className="text-muted-foreground tabular-nums">{byHandler[h] || 0}</span>
                </label>
              ))}
            </div>
            <div className="text-[10px] text-muted-foreground pt-1.5 border-t mt-1">
              Tick several to see everyone&apos;s cases together
            </div>
          </PopoverContent>
        </Popover>
        {/* Sort by */}
        <Select value={sort.by} onValueChange={(v) => setSort(s => ({ ...s, by: v }))}>
          <SelectTrigger className="w-[170px]" data-testid="ageing-sort-by">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="days_past_due">Sort: Days past due</SelectItem>
            <SelectItem value="arrears_amount">Sort: Arrears {cur()}</SelectItem>
            <SelectItem value="balance_amount">Sort: Balance {cur()}</SelectItem>
            <SelectItem value="customer_name">Sort: Customer name</SelectItem>
            <SelectItem value="updated_at">Sort: Last update</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort.dir} onValueChange={(v) => setSort(s => ({ ...s, dir: v }))}>
          <SelectTrigger className="w-[110px]" data-testid="ageing-sort-dir">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">Highest first</SelectItem>
            <SelectItem value="asc">Lowest first</SelectItem>
          </SelectContent>
        </Select>
        {/* Emma #14 — manual status tag filter */}
        <Select value={filter.manual_status || "all"} onValueChange={(v) => setFilter(f => ({ ...f, manual_status: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-[170px]" data-testid="ageing-filter-status">
            <SelectValue placeholder="Status tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status tags</SelectItem>
            {accountStatuses.map(s => <SelectItem key={s.key} value={s.key} data-testid={`ageing-filter-status-${s.key}`}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {(filter.stage || filter.handler || filter.search || filter.dpd_bucket || filter.manual_status) && (
          <Button variant="ghost" size="sm" onClick={() => setFilter({ search: "", handler: "", stage: "", dpd_bucket: "", manual_status: "" })} className="h-9 text-xs">
            <X className="w-3 h-3 mr-1" /> Clear filters
          </Button>
        )}
        <Button variant="outline" size="sm" className="h-9 text-xs" onClick={exportCsv}
          disabled={visibleCases.length === 0} title="Download the filtered view as a CSV"
          data-testid="ageing-export-csv">
          <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Export CSV
        </Button>
        <div className="ml-auto text-xs text-muted-foreground" data-testid="ageing-showing-summary">
          Showing <span className="font-semibold text-slate-700">{visibleCases.length}</span> of {totalCount}
          {airtableFilters.length > 0 && cases.length !== visibleCases.length && (
            <span className="ml-1.5 text-indigo-600">· {cases.length - visibleCases.length} hidden by filters</span>
          )}
        </div>
      </div>

      {/* Off-list search matches — the case exists but fell off the latest
          arrears report, so it isn't in today's worklist (Besure scenario) */}
      {filter.search && offListMatches.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5" data-testid="ageing-offlist-strip">
          <p className="text-xs font-semibold text-amber-900 mb-1.5">
            {offListMatches.length === 1 ? "1 case matches" : `${offListMatches.length} cases match`} your search but {offListMatches.length === 1 ? "isn't" : "aren't"} in today's arrears worklist. Notes and history are preserved on the case file:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {offListMatches.map((m) => (
              <button
                key={m.case_id}
                type="button"
                onClick={() => openCase(m)}
                className="text-[11px] px-2 py-1 rounded border border-amber-300 bg-white hover:bg-amber-100 text-amber-950 transition"
                data-testid={`ageing-offlist-match-${m.case_id}`}
                title={m.held_in_legal
                  ? `Held on the Legal side — ${m.hold_reason || "legal case"}${m.on_latest_report ? " · STILL IN ARREARS on today's LoanDisk report — worked from the Legal Case Tracker" : ""}`
                  : `Dropped off the LoanDisk export (paid up, restructured, or status changed) — off since ${m.off_list_since ? new Date(m.off_list_since).toLocaleDateString("en-GB") : "—"} · last seen ${m.last_arrears_report_at ? new Date(m.last_arrears_report_at).toLocaleDateString("en-GB") : "—"}`}
              >
                <span className="font-semibold">{m.customer_name}</span>
                {m.loan_application_id && <span className="ml-1 font-mono text-amber-700">{m.loan_application_id}</span>}
                <span className="ml-1 text-amber-800">
                  {m.held_in_legal ? (
                    <>· ⚖ held in Legal{m.on_latest_report ? " — still in arrears on today's report" : ""} · open file →</>
                  ) : (
                    <>· off list since {m.off_list_since ? new Date(m.off_list_since).toLocaleDateString("en-GB") : "—"} · open file →</>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bulk action bar — appears only when rows are selected */}
      {selectedIds.size > 0 && (
        <div
          className="flex items-center gap-3 px-4 py-2.5 bg-indigo-50 border-2 border-indigo-200 rounded-md text-sm"
          data-testid="ageing-bulk-bar"
        >
          <span className="font-semibold text-indigo-900">
            {selectedIds.size} selected
          </span>
          <Select disabled={bulkBusy} onValueChange={(v) => handleBulkAssign(v === "_unassign" ? null : v)}>
            <SelectTrigger className="w-[200px] h-8 bg-white" data-testid="ageing-bulk-assign-select">
              <SelectValue placeholder={bulkBusy ? "Assigning…" : "Assign to handler…"} />
            </SelectTrigger>
            <SelectContent>
              {handlers.map(h => (
                <SelectItem key={h} value={h} data-testid={`ageing-bulk-handler-${h}`}>{h}</SelectItem>
              ))}
              <SelectItem value="_unassign" data-testid="ageing-bulk-handler-unassign">— Unassign —</SelectItem>
            </SelectContent>
          </Select>
          <Select disabled={bulkBusy} value="" onValueChange={requestBulkStage}>
            <SelectTrigger className="w-[200px] h-8 bg-white" data-testid="ageing-bulk-stage-select">
              <SelectValue placeholder="Move to stage…" />
            </SelectTrigger>
            <SelectContent>
              {stages.map(s => (
                <SelectItem key={s.key} value={s.key} data-testid={`ageing-bulk-stage-${s.key}`}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs ml-auto"
            onClick={() => setSelectedIds(new Set())}
            data-testid="ageing-bulk-clear"
          >
            Clear selection
          </Button>
        </div>
      )}

      {/* Bulk-stage confirmation dialog */}
      <Dialog open={!!bulkStagePending} onOpenChange={(v) => { if (!v) setBulkStagePending(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="bulk-stage-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Move {bulkStagePending?.count} case{bulkStagePending?.count === 1 ? "" : "s"} → {bulkStagePending?.stage}?</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            {bulkStagePending?.stage === "legal" && (
              <div className="rounded border border-red-200 bg-red-50 p-3 text-red-800">
                <div className="font-semibold mb-1">⚠️ This will escalate each case on LoanDisk</div>
                <div className="text-xs">Each loan&apos;s <code className="font-mono">loan_status_id</code> will be set to <strong>3 (Legal)</strong> via PUT /loan/&lt;id&gt;. Cases without a resolvable LoanDisk loan_id will be skipped.</div>
              </div>
            )}
            {bulkStagePending?.stage === "open_loan" && (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-amber-900">
                <div className="font-semibold mb-1">This will set each case back to Open on LoanDisk</div>
                <div className="text-xs">Each loan&apos;s <code className="font-mono">loan_status_id</code> will be set to <strong>1 (Open)</strong>. Use this when reverting a misfiled legal escalation.</div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkStagePending(null)} data-testid="bulk-stage-cancel-btn">Cancel</Button>
            <Button
              onClick={confirmBulkStage}
              disabled={bulkBusy}
              className={bulkStagePending?.stage === "legal" ? "bg-red-600 hover:bg-red-700" : ""}
              data-testid="bulk-stage-confirm-btn"
            >
              {bulkBusy ? "Updating…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Active filter chips strip */}
      <FilterChips
        conditions={airtableFilters}
        fields={filterFields}
        onRemove={(id) => setAirtableFilters(prev => prev.filter(c => c.id !== id))}
        onClear={() => setAirtableFilters([])}
      />

      {/* Cases table */}
      <Card>
        <CardContent className="p-0">
          {loading && cases.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading cases…
            </div>
          ) : visibleCases.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground space-y-2">
              {airtableFilters.length > 0 ? (
                <>
                  <div>No cases match your filters.</div>
                  <Button variant="outline" size="sm" onClick={() => setAirtableFilters([])}>
                    <X className="w-3.5 h-3.5 mr-2" /> Clear filters
                  </Button>
                </>
              ) : (
                <>
                  <div>No cases yet.</div>
                  <Button variant="outline" size="sm" onClick={() => setUploadOpen(true)}>
                    <Upload className="w-3.5 h-3.5 mr-2" /> Upload your first arrears report
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-280px)]" data-testid="ageing-table-scroll">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase text-muted-foreground border-b sticky top-0 z-10">
                  <tr>
                    <th className="w-10 px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={allSelected ? true : someSelected ? "indeterminate" : false}
                        onCheckedChange={toggleAllVisible}
                        aria-label="Select all visible cases"
                        data-testid="ageing-select-all"
                      />
                    </th>
                    <th className="text-left px-3 py-2 font-semibold">Customer</th>
                    <th className="text-left px-3 py-2 font-semibold">SF#</th>
                    <th className="text-center px-3 py-2 font-semibold">DPD</th>
                    <th className="text-right px-3 py-2 font-semibold">Arrears {cur()}</th>
                    <th className="text-right px-3 py-2 font-semibold">Principal Out {cur()}</th>
                    <th className="text-right px-3 py-2 font-semibold">Total Out {cur()}</th>
                    <th className="text-left px-3 py-2 font-semibold">Stage</th>
                    <th className="text-left px-3 py-2 font-semibold">Handler</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibleCases.map(c => {
                    const isSel = selectedIds.has(c.case_id);
                    const isRestructured = /\/\d+$/.test((c.loan_application_id || "").trim());
                    const directorLabel = c.borrower_director_name && c.borrower_director_name !== c.customer_name
                      ? c.borrower_director_name : null;
                    const principalOut = c.total_principal_outstanding ?? c.principal_outstanding ?? 0;
                    return (
                    <tr
                      key={c.case_id}
                      className={`border-b hover:bg-slate-50 group ${isSel ? "bg-indigo-50/50" : ""}`}
                      data-testid={`ageing-row-${c.case_id}`}
                    >
                      <td
                        className="px-3 py-2 cursor-pointer align-top pt-3"
                        onClick={(e) => { e.stopPropagation(); toggleRow(c.case_id); }}
                        data-testid={`ageing-row-checkbox-${c.case_id}`}
                      >
                        <Checkbox
                          checked={isSel}
                          aria-label={`Select ${c.customer_name}`}
                          className="pointer-events-none"
                        />
                      </td>
                      <td className="px-3 py-2 cursor-pointer align-top" onClick={() => openCase(c)}>
                        <div className="font-medium leading-tight inline-flex items-center gap-1.5">
                          <ContactabilityDot dot={contactability[c.case_id]} />
                          <span className="truncate" data-testid={`ageing-row-customer-${c.case_id}`}>
                            {c.customer_name || "—"}
                          </span>
                        </div>
                        {(directorLabel || c.loan_officer) && (
                          <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                            {directorLabel && <span>{directorLabel}</span>}
                            {directorLabel && c.loan_officer && <span className="mx-1 opacity-60">·</span>}
                            {c.loan_officer && <span className="text-slate-500">{c.loan_officer}</span>}
                          </div>
                        )}
                        {c.breathing_space?.active && (
                          <Badge
                            className="bg-purple-600 text-white border-purple-700 text-[9px] font-bold tracking-wider px-1.5 h-4 hover:bg-purple-700 mt-1"
                            data-testid={`ageing-row-bs-${c.case_id}`}
                            title={`${rl("hold")} active${c.breathing_space.subjects?.length ? ` for ${c.breathing_space.subjects.join(", ")}` : ""} — advisor ${c.breathing_space.debt_advisor || "?"}${c.breathing_space.end_date ? ` · ends ${c.breathing_space.end_date}` : ""} — do not contact`}
                          >
                            ⏸ BS PROTECTED{c.breathing_space.active_count > 1 ? ` ×${c.breathing_space.active_count}` : ""}
                          </Badge>
                        )}
                        {c.legal_hold?.active && (
                          <Badge
                            variant="outline"
                            className="bg-amber-100 text-amber-900 border-amber-300 text-[9px] font-bold tracking-wider px-1.5 h-4 mt-1"
                            data-testid={`ageing-row-legal-ongoing-${c.case_id}`}
                            title={`Legal ongoing — ${c.legal_hold.reason || "legal action still live"}${c.legal_handler ? ` · legal owner ${c.legal_handler}` : ""}. Account stays with Collections.`}
                          >
                            ⚖ LEGAL ONGOING{c.legal_handler ? ` · ${c.legal_handler.split(" ")[0]}` : ""}
                          </Badge>
                        )}
                        {c.manual_statuses?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1" data-testid={`ageing-row-status-tags-${c.case_id}`}>
                            <StatusTagChips keys={c.manual_statuses} statuses={accountStatuses} testid="ageing-status-tag" />
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground cursor-pointer align-top" onClick={() => openCase(c)}>
                        <div className="flex flex-col items-start gap-1">
                          <span data-testid={`ageing-row-sf-${c.case_id}`}>{c.loan_application_id || "—"}</span>
                          {isRestructured && (
                            <Badge
                              className="bg-red-600 text-white border-red-700 text-[9px] font-bold tracking-wider px-1.5 h-4 hover:bg-red-700"
                              data-testid={`ageing-row-restructured-${c.case_id}`}
                            >
                              RESTRUCTURED
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center cursor-pointer align-top pt-3" onClick={() => openCase(c)}>
                        <span className={`inline-flex items-center justify-center min-w-[44px] px-2 py-0.5 rounded-full text-xs font-bold tabular-nums ${dpdRowTone(c.days_past_due)}`}>
                          {c.days_past_due ?? "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums cursor-pointer text-rose-700 font-medium align-top pt-3" onClick={() => openCase(c)}>{fmtGBP(c.arrears_amount || 0)}</td>
                      <td
                        className="px-3 py-2 text-right tabular-nums cursor-pointer text-slate-600 align-top pt-3"
                        onClick={() => openCase(c)}
                        data-testid={`ageing-row-principal-${c.case_id}`}
                      >
                        {fmtGBP(principalOut)}
                      </td>
                      <td
                        className="px-3 py-2 text-right tabular-nums cursor-pointer font-semibold text-slate-900 align-top pt-3"
                        onClick={() => openCase(c)}
                        data-testid={`ageing-row-total-${c.case_id}`}
                      >
                        {fmtGBP(c.balance_amount || 0)}
                      </td>
                      <td className="px-3 py-2 cursor-pointer align-top pt-3" onClick={() => openCase(c)}>
                        <Badge variant="outline" className={`text-[10px] ${STAGE_TONES[c.stage] || ""}`}>
                          {stageMeta[c.stage]?.label || c.stage || "—"}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs cursor-pointer align-top pt-3" onClick={() => openCase(c)}>
                        {c.handler
                          ? <span className="font-medium">{c.handler}</span>
                          : <span className="text-muted-foreground italic">Unassigned</span>}
                      </td>
                      <td className="px-3 py-2 text-right cursor-pointer align-top pt-3" onClick={() => openCase(c)}>
                        <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                {/* Total row — sums the currently visible (filter-respecting)
                    case list. Distinct from the global arrearsTotal /
                    balanceTotal at the top, which always reflects the full
                    book. Helps ops see "what's in front of me right now"
                    after applying a stage / handler / DPD filter. */}
                {visibleCases.length > 0 && (() => {
                  const tArrears   = visibleCases.reduce((a, c) => a + (c.arrears_amount || 0), 0);
                  const tPrincipal = visibleCases.reduce((a, c) => a + (c.total_principal_outstanding ?? c.principal_outstanding ?? 0), 0);
                  const tBalance   = visibleCases.reduce((a, c) => a + (c.balance_amount || 0), 0);
                  return (
                    <tfoot className="bg-slate-50 border-t-2 border-slate-300 sticky bottom-0">
                      <tr className="text-sm font-semibold" data-testid="ageing-totals-row">
                        <td className="px-3 py-2.5" colSpan={3}>
                          <span className="text-[10px] uppercase tracking-wider text-slate-500">Totals · </span>
                          <span className="text-slate-700">{visibleCases.length} {visibleCases.length === 1 ? "case" : "cases"}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-rose-700" data-testid="ageing-totals-arrears">
                          {fmtGBPdp(tArrears)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-700" data-testid="ageing-totals-principal">
                          {fmtGBPdp(tPrincipal)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-900" data-testid="ageing-totals-balance">
                          {fmtGBPdp(tBalance)}
                        </td>
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      </>
      )}

      {/* Upload dialog */}
      <UploadArrearsDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => {
          setUploadOpen(false);
          reload();
          listArrearsSnapshots(120).then(r => setHistoryList(r.snapshots || [])).catch(() => {});
        }}
      />
    </div>
  );
}

// "Fri 4 Jul 2026 09:30 · 108 cases" — history dropdown option label
function formatSnapshotOption(s) {
  const d = s.uploaded_at ? new Date(s.uploaded_at) : null;
  const when = d
    ? d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
      + " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : s.snapshot_id;
  const cases = s.totals?.row_count ?? s.metrics?.row_count;
  return cases != null ? `${when} · ${cases} cases` : when;
}

// ---------------------------------------------------------------------------
// UploadArrearsDialog — file picker → POST /upload-arrears
// ---------------------------------------------------------------------------
function UploadArrearsDialog({ open, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!file) {
      toast.error("Pick a file first");
      return;
    }
    try {
      setUploading(true);
      const r = await uploadArrearsReport(file);
      setLastResult(r);
      const ps = r.parse_stats || {};
      const raw = ps.raw_excel_rows;
      const detailParts = [
        `${r.created} new`,
        `${r.refreshed} refreshed`,
      ];
      if (r.off_list_flagged) detailParts.push(`${r.off_list_flagged} fell off`);
      const ht = r.handler_tagging || {};
      if (ht.applied) detailParts.push(`${ht.applied} handler${ht.applied === 1 ? "" : "s"} tagged`);
      if (ht.unmatched_names?.length) {
        toast.warning(
          `Handler name${ht.unmatched_names.length === 1 ? "" : "s"} not recognised (rows left untagged): ${ht.unmatched_names.join(", ")}. Portal handlers: Niko, Juffrey, John, Mia, Irish, Robert, Emma.`,
          { duration: 12000 },
        );
      }
      const headline = raw && raw !== r.parsed_rows
        ? `Parsed ${r.parsed_rows} loans from ${raw} spreadsheet rows`
        : `Parsed ${r.parsed_rows} loans`;
      toast.success(`${headline} · ${detailParts.join(" · ")}`, { duration: 7000 });
      setFile(null);
      onUploaded();
    } catch (e) {
      const msg = errorDetailMessage(e, e.message);
      toast.error("Upload failed: " + msg, { duration: 9000 });
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-md" data-testid="ageing-upload-dialog">
        <SheetHeader>
          <SheetTitle>Upload daily arrears report</SheetTitle>
          <SheetDescription>
            Drop today's LoanDisk arrears export here (CSV or XLSX). We'll match each row to an existing case by LoanDisk loan ID and create new cases for anyone who hasn't been seen before. Tip: add a <b>Handler</b> column with a team member's name on each row to bulk-assign handlers in the same upload.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          <div
            className="border-2 border-dashed rounded-md p-6 text-center cursor-pointer hover:bg-slate-50"
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            {file
              ? <div className="text-sm font-medium">{file.name}<div className="text-xs text-muted-foreground mt-1">{(file.size / 1024).toFixed(1)} KB</div></div>
              : <div className="text-sm text-muted-foreground">Click to pick a file<div className="text-xs mt-1">CSV or XLSX</div></div>
            }
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="ageing-upload-file-input"
            />
          </div>
          <Button onClick={submit} disabled={!file || uploading} className="w-full" data-testid="ageing-upload-submit">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <Upload className="w-3.5 h-3.5 mr-2" />}
            Upload &amp; parse
          </Button>
          {lastResult && lastResult.parse_stats && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-1.5 text-xs" data-testid="ageing-upload-result">
              <div className="font-semibold text-emerald-900 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                Parsed {lastResult.parsed_rows} loans from {lastResult.parse_stats.raw_excel_rows} spreadsheet rows
              </div>
              <ul className="text-emerald-900/80 space-y-0.5 pl-5 list-disc">
                <li>{lastResult.parse_stats.title_rows} title row(s)</li>
                <li>1 header row</li>
                <li className="font-medium">{lastResult.parse_stats.parsed_rows} loan rows ingested</li>
                {lastResult.parse_stats.skipped_totals > 0 && (
                  <li>{lastResult.parse_stats.skipped_totals} totals/footer row(s) skipped</li>
                )}
                {lastResult.parse_stats.skipped_no_loan_ref > 0 && (
                  <li className="text-amber-700">{lastResult.parse_stats.skipped_no_loan_ref} row(s) missing Loan# — skipped</li>
                )}
                {lastResult.handler_tagging?.applied > 0 && (
                  <li className="font-medium text-indigo-800" data-testid="ageing-upload-handlers-tagged">
                    {lastResult.handler_tagging.applied} handler assignment(s) applied from the Handler column
                  </li>
                )}
                {lastResult.handler_tagging?.unmatched_names?.length > 0 && (
                  <li className="text-amber-700" data-testid="ageing-upload-handlers-unmatched">
                    Unrecognised handler name(s): {lastResult.handler_tagging.unmatched_names.join(", ")}
                  </li>
                )}
                {lastResult.parse_stats.skipped_blank > 0 && (
                  <li>{lastResult.parse_stats.skipped_blank} blank row(s) skipped</li>
                )}
              </ul>
              <div className="text-emerald-900/80 pt-1 border-t border-emerald-200">
                <span className="font-medium">{lastResult.created}</span> new ·{" "}
                <span className="font-medium">{lastResult.refreshed}</span> refreshed
                {lastResult.off_list_flagged > 0 && (
                  <> · <span className="font-medium text-rose-700">{lastResult.off_list_flagged}</span> fell off</>
                )}
              </div>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground italic">
            ⓘ Parser activates once we've seen a sample export from your daily LoanDisk arrears report — until then uploads save the raw file for inspection only.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// CaseDrawer — the central workflow surface. LoanDisk info + notes timeline +
// handler/stage controls. Aircall + Monday + Outlook panels arrive in Phase 2/3.
// ---------------------------------------------------------------------------
function CaseDrawer({ caseRow, open, onClose, onChanged, handlers, roster, stages }) {
  const [edit, setEdit] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [newNoteType, setNewNoteType] = useState("note");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (caseRow) setEdit({ handler: caseRow.handler || "", stage: caseRow.stage || "open_loan" });
    setNewNote("");
    setNewNoteType("note");
  }, [caseRow]);

  if (!caseRow || !edit) return null;

  const persistFields = async (fields) => {
    try {
      setSaving(true);
      await updateAgeingCase(caseRow.case_id, { ...fields, actor: "Ops" });
      toast.success("Saved");
      onChanged();
    } catch (e) {
      toast.error("Save failed: " + errorDetailMessage(e, e.message));
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      setSaving(true);
      await addAgeingNote(caseRow.case_id, { text: newNote.trim(), note_type: newNoteType });
      toast.success("Note added");
      setNewNote("");
      onChanged();
    } catch (e) {
      toast.error("Failed to add note: " + errorDetailMessage(e, e.message));
    } finally {
      setSaving(false);
    }
  };

  const removeNote = async (noteId) => {
    if (!confirm("Delete this note?")) return;
    try {
      await deleteAgeingNote(caseRow.case_id, noteId);
      onChanged();
    } catch (e) {
      toast.error("Failed: " + errorDetailMessage(e, e.message));
    }
  };

  const stageMeta = stages.find(s => s.key === edit.stage);
  const notes = [...(caseRow.notes || [])].sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
        <SheetHeader className="pb-3 border-b">
          <SheetTitle className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="truncate text-lg" data-testid="ageing-drawer-customer">{caseRow.customer_name || "—"}</div>
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground font-normal">
                <span>{caseRow.loan_application_id || "no SF#"}</span>
                {caseRow.days_past_due != null && <span>· {caseRow.days_past_due} days past due</span>}
                {caseRow.first_seen_in_arrears && <span>· first seen {caseRow.first_seen_in_arrears.slice(0, 10)}</span>}
              </div>
            </div>
            <Badge variant="outline" className={`text-xs ${STAGE_TONES[edit.stage] || ""}`}>
              {stageMeta?.label || edit.stage}
            </Badge>
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-5 mt-5">
          {/* §1 Workflow controls — stage + handler */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><ArrowRightCircle className="w-4 h-4" /> Workflow</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Stage</Label>
                <Select value={edit.stage} onValueChange={(v) => { setEdit({ ...edit, stage: v }); persistFields({ stage: v }); }}>
                  <SelectTrigger data-testid="ageing-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {stages.map(s => (
                      <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Handler</Label>
                <Select value={edit.handler || "_unassigned"} onValueChange={(v) => { const next = v === "_unassigned" ? "" : v; setEdit({ ...edit, handler: next }); persistFields({ handler: next, handler_user_id: handlerIdFor(roster, next) }); }}>
                  <SelectTrigger data-testid="ageing-handler-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_unassigned">Unassigned</SelectItem>
                    {handlers.map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* §2 LoanDisk snapshot — live-pull on drawer open */}
          <LoanDiskSnapshotCard caseRow={caseRow} />

          {/* §3 Aircall + Monday + Outlook — live integrations */}
          <AircallCallsCard caseRow={caseRow} />
          <AircallSmsCard caseRow={caseRow} />
          <MondayCardPanel caseRow={caseRow} />
          <OutlookEmailCard caseRow={caseRow} />

          {/* §4 Notes / Timeline */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Promises to Pay</CardTitle></CardHeader>
            <CardContent>
              <P2PPanel caseRow={caseRow} onChanged={onChanged} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> Activity Timeline</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {/* Add new note */}
              <div className="border rounded-md p-3 bg-slate-50/50 space-y-2">
                <div className="flex gap-2">
                  <Select value={newNoteType} onValueChange={setNewNoteType}>
                    <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="ageing-note-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="note">Note</SelectItem>
                      <SelectItem value="call">Call</SelectItem>
                      <SelectItem value="email_out">Email out</SelectItem>
                      <SelectItem value="email_in">Email in</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Spoke to customer · agreed payment plan · sent email re: arrears…"
                  rows={3}
                  className="text-sm"
                  data-testid="ageing-note-input"
                />
                <Button size="sm" onClick={addNote} disabled={!newNote.trim() || saving} className="w-full" data-testid="ageing-add-note-btn">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <Send className="w-3 h-3 mr-2" />}
                  Log to timeline
                </Button>
              </div>

              {/* Timeline */}
              {notes.length === 0 ? (
                <div className="text-center py-6 text-xs text-muted-foreground">
                  No activity yet — log the first call / email / note above.
                </div>
              ) : (
                <div className="space-y-2">
                  {notes.map(n => {
                    const meta = NOTE_TYPE_META[n.type] || NOTE_TYPE_META.note;
                    const Icon = meta.icon;
                    return (
                      <div key={n.id} className="flex gap-2 text-xs border-l-2 border-slate-200 pl-3 py-1.5 group">
                        <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${meta.tone}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-[10px] text-muted-foreground uppercase tracking-wide">
                            <span className="font-semibold">{meta.label}</span>
                            <span>·</span>
                            <span className="normal-case break-all">{n.author}</span>
                            <span>·</span>
                            <span>{n.timestamp ? new Date(n.timestamp).toLocaleString("en-GB") : ""}</span>
                          </div>
                          <div className="text-sm whitespace-pre-wrap break-words">{n.text}</div>
                        </div>
                        <button
                          onClick={() => removeNote(n.id)}
                          className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-700 transition-opacity"
                          title="Delete note"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 sticky bottom-0 bg-background pt-3 mt-5 border-t">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Small stat label/value tile (used in LoanDisk snapshot)
export function Stat({ label, value, sub, tone }) {
  const toneClass = tone === "red" ? "text-red-700" : tone === "amber" ? "text-amber-700" : "";
  return (
    <div className="rounded-md border bg-slate-50/40 px-3 py-2">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${toneClass}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

// "On list" badge — shows whether the case is in TODAY's arrears report or
// off-list (paid up). Today = same calendar date as the latest report seen.
export function OnListBadge({ lastSeen }) {
  if (!lastSeen) return <span className="text-muted-foreground italic">never</span>;
  const seen = new Date(lastSeen);
  const ageDays = Math.floor((Date.now() - seen.getTime()) / 86400000);
  if (ageDays <= 1) {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700 text-[11px]">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" /> In today&apos;s report
      </span>
    );
  }
  return (
    <span className="text-[11px] text-muted-foreground">
      Off list {ageDays}d
    </span>
  );
}

// LoanDisk live snapshot — fetches the live loan record + recent repayments
// from LoanDisk via our backend proxy. Auto-loads on drawer open, with a
// manual refresh button.
export function LoanDiskSnapshotCard({ caseRow }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchSnapshot = useCallback(async () => {
    if (!caseRow?.case_id) return;
    try {
      setLoading(true);
      setErr(null);
      const r = await getAgeingLoanDiskSnapshot(caseRow.case_id, { repayments_limit: 20 });
      setData(r);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [caseRow?.case_id]);

  useEffect(() => { fetchSnapshot(); }, [fetchSnapshot]);

  const summary = data?.summary || {};
  const repayments = data?.repayments || [];
  const fetchedAt = data?.fetched_at ? new Date(data.fetched_at).toLocaleTimeString("en-GB") : null;
  const ldLoanId = summary.loan_id;

  return (
    <Card data-testid="ageing-loandisk-snapshot-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
        <CardTitle className="text-sm flex items-center gap-2">
          LoanDisk snapshot
          {fetchedAt && !loading && (
            <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">
              Fetched {fetchedAt}
            </Badge>
          )}
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </CardTitle>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={fetchSnapshot} disabled={loading} data-testid="ageing-snapshot-refresh">
            <Loader2 className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : "hidden"}`} />
            Refresh
          </Button>
          {ldLoanId && (
            <Button size="sm" variant="outline" asChild className="h-7 px-2 text-[11px]" data-testid="ageing-open-loandisk-btn">
              <a href={`https://app.loandisk.com/loan/view/${ldLoanId}`} target="_blank" rel="noreferrer">
                Open in LoanDisk <ExternalLink className="w-3 h-3 ml-1.5" />
              </a>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {err && (
          <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {err}
          </div>
        )}

        {/* Borrower contact strip */}
        {summary.borrower_business && (
          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
            <span><span className="font-medium text-slate-700">{summary.borrower_business}</span></span>
            {summary.borrower_name && <span>{summary.borrower_name}</span>}
            {summary.borrower_mobile && <a href={`tel:${summary.borrower_mobile}`} className="underline-offset-2 hover:underline">{summary.borrower_mobile}</a>}
            {summary.borrower_email && <a href={`mailto:${summary.borrower_email}`} className="underline-offset-2 hover:underline">{summary.borrower_email}</a>}
          </div>
        )}

        {/* Money grid */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <Stat label="Principal lent" value={summary.principal_amount != null ? fmtGBPdp(summary.principal_amount) : "—"} />
          <Stat label="Balance" value={summary.balance_amount != null ? fmtGBPdp(summary.balance_amount) : "—"} />
          <Stat
            label="Days past due"
            value={summary.days_past_due ?? "—"}
            tone={(summary.days_past_due || 0) >= 60 ? "red" : (summary.days_past_due || 0) >= 30 ? "amber" : null}
          />
          <Stat label="Interest outstanding" value={summary.interest_balance != null ? fmtGBPdp(summary.interest_balance) : "—"} />
          <Stat label="Fees outstanding" value={summary.fees_balance != null ? fmtGBPdp(summary.fees_balance) : "—"} tone={(summary.fees_balance || 0) > 0 ? "amber" : null} />
          <Stat label="Penalty outstanding" value={summary.penalty_balance != null ? fmtGBPdp(summary.penalty_balance) : "—"} tone={(summary.penalty_balance || 0) > 0 ? "red" : null} />
          <Stat label="Rate" value={summary.interest_rate ? `${parseFloat(summary.interest_rate).toFixed(0)}% / ${summary.interest_period || "—"}` : "—"} />
          <Stat label="Repayments" value={summary.num_of_repayments ?? "—"} sub={summary.repayment_frequency || null} />
          <Stat label="Next due" value={summary.due_date || "—"} />
        </div>

        {/* Repayments received */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
              Recent repayments {repayments.length > 0 && <span className="text-slate-700">({repayments.length})</span>}
            </div>
          </div>
          {loading ? (
            <div className="text-xs text-muted-foreground py-3 text-center">
              <Loader2 className="w-3 h-3 animate-spin inline mr-1.5" /> Loading…
            </div>
          ) : repayments.length === 0 ? (
            <div className="text-xs text-muted-foreground italic py-3 text-center border border-dashed rounded">
              No repayments recorded against this loan yet.
            </div>
          ) : (
            <div className="border rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-muted-foreground border-b">
                  <tr>
                    <th className="text-left px-3 py-1.5">Date collected</th>
                    <th className="text-right px-3 py-1.5">Amount</th>
                    <th className="text-left px-3 py-1.5">Method</th>
                    <th className="text-left px-3 py-1.5">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {repayments.map(r => (
                    <tr key={r.repayment_id} className="border-b last:border-b-0">
                      <td className="px-3 py-1.5 font-mono">{r.repayment_collected_date || "—"}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtGBPdp(parseFloat(r.repayment_amount || 0))}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">#{r.loan_repayment_method_id ?? "—"}</td>
                      <td className="px-3 py-1.5 text-muted-foreground truncate max-w-[200px]">{r.repayment_notes || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Hint to deep-link for the full LD schedule */}
        <p className="text-[11px] text-muted-foreground italic">
          LoanDisk&apos;s full loan-schedule grid (forecast rows, penalties, etc) lives only in their UI — click <strong>Open in LoanDisk</strong> above for the full table.
        </p>
      </CardContent>
    </Card>
  );
}


// ─── Aircall calls panel — pulls recent calls for the borrower's mobile ─────
export function AircallCallsCard({ caseRow }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchCalls = useCallback(async () => {
    if (!caseRow?.case_id) return;
    try {
      setLoading(true);
      setErr(null);
      const r = await getAgeingAircallCalls(caseRow.case_id, { limit: 25 });
      setData(r);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [caseRow?.case_id]);
  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  const calls = data?.calls || [];
  const phone = data?.phone_raw;

  return (
    <Card data-testid="ageing-aircall-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Phone className="w-4 h-4 text-emerald-600" />
          Aircall call history
          {phone && <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">{phone}</Badge>}
        </CardTitle>
        <div className="flex items-center gap-1.5">
          {phone && (
            <Button size="sm" variant="outline" asChild className="h-7 px-2 text-[11px]" data-testid="ageing-aircall-click-to-call">
              <a href={`tel:${phone}`}><Phone className="w-3 h-3 mr-1" /> Call</a>
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={fetchCalls} disabled={loading} data-testid="ageing-aircall-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {err && (
          <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>
        )}
        {!err && data?.warning && (
          <div className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">{data.warning}</div>
        )}
        {loading ? (
          <div className="text-xs text-muted-foreground py-3 text-center">
            <Loader2 className="w-3 h-3 animate-spin inline mr-1.5" /> Searching Aircall…
          </div>
        ) : calls.length === 0 ? (
          !data?.warning && !err && (
            <div className="text-xs text-muted-foreground italic py-3 text-center border border-dashed rounded">
              No calls found for this number in Aircall.
            </div>
          )
        ) : (
          <div className="border rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-muted-foreground border-b">
                <tr>
                  <th className="text-left px-3 py-1.5">When</th>
                  <th className="text-left px-3 py-1.5">Dir</th>
                  <th className="text-left px-3 py-1.5">Status</th>
                  <th className="text-right px-3 py-1.5">Duration</th>
                  <th className="text-left px-3 py-1.5">Agent</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {calls.map(c => (
                  <tr key={c.id} className="border-b last:border-b-0">
                    <td className="px-3 py-1.5 font-mono">{c.started_at ? new Date(c.started_at * 1000).toLocaleString("en-GB") : "—"}</td>
                    <td className="px-3 py-1.5">
                      <span className={c.direction === "inbound" ? "text-emerald-700" : "text-indigo-700"}>{c.direction}</span>
                    </td>
                    <td className="px-3 py-1.5">
                      {c.status === "done" ? <span className="text-slate-700">done</span>
                        : c.status === "missed" ? <span className="text-red-700 font-semibold">missed</span>
                        : c.status === "voicemail" ? <span className="text-amber-700">voicemail</span>
                        : c.status}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{c.duration ? `${c.duration}s` : "—"}</td>
                    <td className="px-3 py-1.5 truncate max-w-[120px]">{c.user_name || "—"}</td>
                    <td className="px-3 py-1.5 text-right">
                      {c.recording && (
                        <a href={c.recording} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline text-[10px]">▶ Recording</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Monday.com card panel — surfaces the workflow item for this customer ───
export function MondayCardPanel({ caseRow }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchCard = useCallback(async () => {
    if (!caseRow?.case_id) return;
    try {
      setLoading(true);
      setErr(null);
      const r = await getAgeingMondayCard(caseRow.case_id);
      setData(r);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [caseRow?.case_id]);
  useEffect(() => { fetchCard(); }, [fetchCard]);

  const card = data?.card;
  return (
    <Card data-testid="ageing-monday-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <FileText className="w-4 h-4 text-pink-600" />
          Monday.com loan file
          {data?.base_sf && <Badge variant="outline" className="text-[10px] font-mono">{data.base_sf}</Badge>}
        </CardTitle>
        {data?.monday_url && (
          <Button size="sm" variant="outline" asChild className="h-7 px-2 text-[11px]" data-testid="ageing-monday-open-btn">
            <a href={data.monday_url} target="_blank" rel="noreferrer">
              Open in Monday <ExternalLink className="w-3 h-3 ml-1.5" />
            </a>
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {err && <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
        {loading ? (
          <div className="text-xs text-muted-foreground py-3 text-center">
            <Loader2 className="w-3 h-3 animate-spin inline mr-1.5" /> Looking up Monday…
          </div>
        ) : !data?.found ? (
          <div className="text-xs text-muted-foreground italic py-3 text-center border border-dashed rounded" data-testid="ageing-monday-not-found">
            {data?.reason || "No Monday card found for this loan."}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm font-semibold text-slate-800">{card.name}</div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <Stat label="Status" value={card.status || "—"} />
              <Stat label="Group" value={card.group_name || "—"} />
              <Stat label="Account manager" value={card.account_manager || "—"} />
              <Stat label="Loan amount" value={card.loan_amount ? fmtGBPdp(card.loan_amount) : "—"} />
              <Stat label="Factor" value={card.factor ?? "—"} />
              <Stat label="Duration" value={card.duration ?? "—"} />
              <Stat label="Broker" value={card.broker_name || "Direct"} sub={card.broker_direct ? "Direct deal" : null} />
              <Stat label="Contact" value={card.contact_name || "—"} sub={card.contact_email || null} />
              <Stat label="Card updated" value={card.updated_at ? card.updated_at.slice(0, 10) : "—"} sub={card.synced_at ? `synced ${card.synced_at.slice(0, 10)}` : null} />
            </div>
            {card.ai_opinion && (
              <details className="border rounded p-2 bg-slate-50/40">
                <summary className="text-[11px] uppercase tracking-wide text-muted-foreground cursor-pointer">AI opinion</summary>
                <div className="text-xs mt-2 text-slate-700 whitespace-pre-wrap">{card.ai_opinion}</div>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Aircall SMS panel — feed + send + embedded Aircall Phone iframe ──────
export function AircallSmsCard({ caseRow }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [phoneOpen, setPhoneOpen] = useState(false);

  const fetchSms = useCallback(async () => {
    if (!caseRow?.case_id) return;
    try {
      setLoading(true);
      setErr(null);
      const r = await getAgeingAircallSms(caseRow.case_id, { limit: 50 });
      setData(r);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [caseRow?.case_id]);
  useEffect(() => { fetchSms(); }, [fetchSms]);

  const messages = data?.messages || [];
  const phone = data?.phone_raw;

  return (
    <Card data-testid="ageing-aircall-sms-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-sky-600" />
          Aircall SMS
          {phone && <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">{phone}</Badge>}
          <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">{messages.length} message{messages.length === 1 ? "" : "s"}</Badge>
        </CardTitle>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => setPhoneOpen(true)} data-testid="ageing-aircall-phone-btn">
            <Phone className="w-3 h-3 mr-1" /> Open Aircall Phone
          </Button>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={fetchSms} disabled={loading} data-testid="ageing-sms-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {err && <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
        {data?.warning && <div className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">{data.warning}</div>}

        {/* Message feed */}
        {loading ? (
          <div className="text-xs text-muted-foreground py-3 text-center">
            <Loader2 className="w-3 h-3 animate-spin inline mr-1.5" /> Loading…
          </div>
        ) : messages.length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-3 text-center border border-dashed rounded">
            No SMS logged for this number yet through the portal. Use <strong>Open Aircall Phone</strong> above to send via the embedded Aircall widget — those SMS will flow back into this feed via webhook.
          </div>
        ) : (
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {messages.map((m, i) => (
              <div key={m.message_id || i} className={`text-xs rounded p-2.5 ${m.direction === "outbound" ? "bg-sky-50 border border-sky-200 ml-8" : "bg-slate-50 border border-slate-200 mr-8"}`}>
                <div className="flex items-center justify-between mb-1 text-[10px] uppercase text-muted-foreground tracking-wide">
                  <span>{m.direction === "outbound" ? "→ Sent" : "← Received"} {m.actor ? `by ${m.actor}` : ""}</span>
                  <span>{m.sent_at ? new Date(m.sent_at).toLocaleString("en-GB") : "—"}</span>
                </div>
                <div className="whitespace-pre-wrap text-slate-800">{m.body}</div>
                {m.status && <div className="text-[10px] text-muted-foreground mt-1">Status: {m.status}</div>}
              </div>
            ))}
          </div>
        )}

        {/* Quick-send removed — API sends showed as "APISMS" with no user
            attribution. All outbound SMS goes through the Aircall dock. */}
        <div className="border-t pt-3 text-[11px] text-muted-foreground bg-slate-50/40 rounded px-3 py-2">
          Outbound SMS goes via <strong>Open Aircall Phone</strong> above — sends from your own Aircall
          number so the timeline shows who sent it. Messages flow back into this feed automatically.
        </div>
      </CardContent>

      {/* Aircall Phone iframe modal */}
      <Dialog open={phoneOpen} onOpenChange={setPhoneOpen}>
        <DialogContent className="max-w-md p-0 sm:max-w-md" data-testid="ageing-aircall-phone-modal">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle className="text-sm flex items-center gap-2">
              <Phone className="w-4 h-4 text-emerald-600" />
              Aircall Phone {phone && <Badge variant="outline" className="text-[10px] font-mono">{phone}</Badge>}
            </DialogTitle>
            <p className="text-[11px] text-muted-foreground">
              Sign in once with your Aircall credentials — calls + SMS sent here flow back into the case timeline via webhook.
            </p>
          </DialogHeader>
          <iframe
            title="Aircall Phone"
            src={phone ? `https://phone.aircall.io/dial?phone_number=${encodeURIComponent(phone)}` : "https://phone.aircall.io/"}
            allow="microphone; autoplay; clipboard-read; clipboard-write"
            className="w-full"
            style={{ height: 600, border: 0 }}
            data-testid="ageing-aircall-phone-iframe"
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Outlook (M365) email panel — full inbox / sent / reply / search ───────
export function OutlookEmailCard({ caseRow, selectedEmailId, onSelectEmail, layout = "card" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [composeMode, setComposeMode] = useState(null);    // null | "new" | { messageId, kind }
  const [folderFilter, setFolderFilter] = useState("all"); // "all" | "Inbox" | "Sent Items" | "Archive"

  const fetchEmails = useCallback(async () => {
    if (!caseRow?.case_id) return;
    try {
      setLoading(true);
      setErr(null);
      const r = await getAgeingM365Emails(caseRow.case_id, { limit: 100 });
      setData(r);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [caseRow?.case_id]);
  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await syncAgeingM365History(caseRow.case_id, { limit: 500 });
      toast.success(`Synced ${r.matched} emails (${r.new_notes} new to timeline)`);
      await fetchEmails();
    } catch (e) {
      toast.error("Sync failed: " + errorDetailMessage(e, e.message));
    } finally {
      setSyncing(false);
    }
  };

  const filteredEmails = useMemo(() => {
    const all = data?.emails || [];
    if (folderFilter === "all") return all;
    if (folderFilter === "Inbox")     return all.filter(e => (e.folder_name || "") === "Inbox");
    if (folderFilter === "Done")      return all.filter(e => (e.folder_name || "").startsWith("Inbox / Done"));
    if (folderFilter === "Sent Items") return all.filter(e => (e.folder_name || "").startsWith("Sent Items"));
    return all;
  }, [data, folderFilter]);

  return (
    <Card data-testid="ageing-outlook-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-600" />
            Outlook · <span className="font-mono text-xs">accounts@simplyfunded.co.uk</span>
            {data?.borrower_email && <Badge variant="outline" className="text-[10px] font-mono">{data.borrower_email}</Badge>}
            {data?.sf_searched && <Badge variant="outline" className="text-[10px] font-mono">{data.sf_searched}</Badge>}
          </CardTitle>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => setSearchOpen(true)} data-testid="ageing-email-search-btn">
              <Search className="w-3 h-3 mr-1" /> Search inbox
            </Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={handleSync} disabled={syncing} data-testid="ageing-email-sync-btn">
              {syncing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ArrowRightCircle className="w-3 h-3 mr-1" />}
              Sync history
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={fetchEmails} disabled={loading}>
              {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Refresh
            </Button>
            <Button size="sm" variant="default" className="h-7 px-2 text-[11px]" onClick={() => setComposeMode("new")} data-testid="ageing-email-compose-toggle">
              <Mail className="w-3 h-3 mr-1" /> Compose
            </Button>
          </div>
        </div>
        {/* Folder tabs */}
        <div className="flex items-center gap-1 mt-2 text-[11px]">
          {["all", "Inbox", "Done", "Sent Items"].map(f => {
            let count;
            if (f === "all") count = (data?.emails || []).length;
            else if (f === "Inbox") count = (data?.emails || []).filter(e => (e.folder_name || "") === "Inbox").length;
            else if (f === "Done") count = (data?.emails || []).filter(e => (e.folder_name || "").startsWith("Inbox / Done")).length;
            else count = (data?.emails || []).filter(e => (e.folder_name || "").startsWith(f)).length;
            return (
              <button
                key={f}
                onClick={() => setFolderFilter(f)}
                className={`px-2.5 py-1 rounded ${folderFilter === f ? "bg-blue-100 text-blue-900 font-semibold" : "text-muted-foreground hover:bg-slate-100"}`}
                data-testid={`ageing-email-folder-${f.replace(/\s/g, "-")}`}
              >
                {f === "all" ? "All" : f} ({count})
              </button>
            );
          })}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {err && <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}

        {/* Email feed */}
        {loading ? (
          <div className="text-xs text-muted-foreground py-3 text-center">
            <Loader2 className="w-3 h-3 animate-spin inline mr-1.5" /> Loading…
          </div>
        ) : filteredEmails.length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-3 text-center border border-dashed rounded">
            No emails in {folderFilter === "all" ? "the mailbox" : folderFilter} match this customer. Click <strong>Sync history</strong> to scan back further.
          </div>
        ) : (
          <div className="border rounded overflow-hidden max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-muted-foreground border-b sticky top-0">
                <tr>
                  <th className="text-left px-3 py-1.5">When</th>
                  <th className="text-left px-3 py-1.5">Folder</th>
                  <th className="text-left px-3 py-1.5">From</th>
                  <th className="text-left px-3 py-1.5">Subject</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredEmails.map(e => {
                  const sender = (e.from?.emailAddress?.address) || "—";
                  const isOutbound = (e.folder_name || "").startsWith("Sent Items");
                  return (
                    <tr key={e.id} className="border-b last:border-b-0 hover:bg-slate-50">
                      <td className="px-3 py-1.5 font-mono text-[11px] text-muted-foreground whitespace-nowrap">{e.receivedDateTime ? new Date(e.receivedDateTime).toLocaleString("en-GB") : "—"}</td>
                      <td className="px-3 py-1.5">
                        <Badge variant="outline" className={`text-[10px] ${isOutbound ? "border-indigo-300 text-indigo-700" : "border-emerald-300 text-emerald-700"}`}>
                          {e.folder_name || "—"}
                        </Badge>
                      </td>
                      <td className="px-3 py-1.5 truncate max-w-[170px]">{sender}</td>
                      <td className="px-3 py-1.5 truncate max-w-[260px]" title={e.bodyPreview || ""}>
                        {!e.isRead && <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mr-1.5" />}
                        {e.subject || "(no subject)"}
                        {e.hasAttachments && <span className="ml-1 text-muted-foreground" title="Has attachments">📎</span>}
                      </td>
                      <td className="px-3 py-1.5 text-right whitespace-nowrap">
                        <button onClick={() => setComposeMode({ kind: "reply", messageId: e.id, originalSubject: e.subject, originalFrom: sender })} className="text-blue-600 hover:underline text-[10px] mr-2" data-testid="ageing-email-reply-btn">↩ Reply</button>
                        {e.webLink && (
                          <a href={e.webLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-[10px]">Open ↗</a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {composeMode && (
          <EmailComposer
            caseRow={caseRow}
            mode={composeMode}
            borrowerEmail={data?.borrower_email}
            sfSearched={data?.sf_searched}
            onClose={() => setComposeMode(null)}
            onSent={async () => { setComposeMode(null); await fetchEmails(); }}
          />
        )}
      </CardContent>

      <SearchInboxDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </Card>
  );
}

// ─── Email composer (handles both new + reply + replyAll) ──────────────────
export function EmailComposer({ caseRow, mode, borrowerEmail, sfSearched, onClose, onSent }) {
  const isReply = mode?.kind === "reply" || mode?.kind === "replyAll";
  const [to, setTo] = useState(borrowerEmail || "");
  const [subject, setSubject] = useState(
    isReply
      ? (mode.originalSubject?.startsWith("Re:") ? mode.originalSubject : `Re: ${mode.originalSubject || ""}`)
      : (sfSearched ? `Re: ${sfSearched} — ` : "")
  );
  const [body, setBody] = useState("");
  const [replyAll, setReplyAll] = useState(false);
  const [sending, setSending] = useState(false);
  const [originalBody, setOriginalBody] = useState("");

  // Fetch the original message body for reply context
  useEffect(() => {
    if (!isReply || !mode.messageId) return;
    (async () => {
      try {
        const r = await getAgeingM365EmailFull(caseRow.case_id, mode.messageId);
        const html = r.message?.body?.content || "";
        setOriginalBody(html);
      } catch (e) {
        // non-fatal
      }
    })();
  }, [isReply, mode, caseRow?.case_id]);

  const handleSend = async () => {
    if (isReply) {
      if (!body.trim()) { toast.error("Reply body is empty."); return; }
      setSending(true);
      try {
        await replyAgeingM365Email(caseRow.case_id, mode.messageId, { body, reply_all: replyAll, actor: "Ops" });
        toast.success(`Reply${replyAll ? "-All" : ""} sent`);
        await onSent();
      } catch (e) {
        toast.error("Reply failed: " + errorDetailMessage(e, e.message));
      } finally {
        setSending(false);
      }
    } else {
      if (!to.trim() || !subject.trim() || !body.trim()) { toast.error("To, subject and body are all required."); return; }
      setSending(true);
      try {
        await sendAgeingM365Email(caseRow.case_id, {
          to: to.split(",").map(s => s.trim()).filter(Boolean),
          subject, body, content_type: "HTML", actor: "Ops",
        });
        toast.success("Email sent");
        await onSent();
      } catch (e) {
        toast.error("Send failed: " + errorDetailMessage(e, e.message));
      } finally {
        setSending(false);
      }
    }
  };

  return (
    <div className="border-2 border-blue-300 rounded p-3 bg-blue-50/30 space-y-2" data-testid="ageing-email-composer">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-blue-900">
          {isReply ? (replyAll ? "Reply All" : "Reply") + " · " + (mode.originalFrom || "") : "New email"}
        </div>
        <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
      </div>
      {!isReply && (
        <>
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="To: (comma-separated)" className="h-8 text-xs bg-white" data-testid="ageing-email-to" />
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="h-8 text-xs bg-white" data-testid="ageing-email-subject" />
        </>
      )}
      {isReply && (
        <div className="text-[11px] text-muted-foreground">Subject: <span className="font-mono">{subject}</span></div>
      )}
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={isReply ? 5 : 7} placeholder={isReply ? "Type your reply…" : "Message body…"} className="text-xs bg-white" data-testid="ageing-email-body" />
      {isReply && (
        <div className="flex items-center gap-2 text-[11px]">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={replyAll} onChange={(e) => setReplyAll(e.target.checked)} className="rounded" />
            Reply to all recipients
          </label>
        </div>
      )}
      {isReply && originalBody && (
        <details className="border rounded p-2 bg-white text-[11px]">
          <summary className="cursor-pointer text-muted-foreground">Show original message</summary>
          <div className="mt-2 max-h-[200px] overflow-y-auto" dangerouslySetInnerHTML={sanitizedHtmlProps(originalBody)} />
        </details>
      )}
      <div className="flex items-center justify-between pt-1">
        <span className="text-[10px] text-muted-foreground">Sending as <span className="font-mono">accounts@simplyfunded.co.uk</span> · signature auto-appended</span>
        <Button size="sm" onClick={handleSend} disabled={sending} className="h-8 text-xs" data-testid="ageing-email-send-btn">
          {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
          {isReply ? (replyAll ? "Send reply-all" : "Send reply") : "Send email"}
        </Button>
      </div>
    </div>
  );
}

// ─── Free-text inbox search dialog ─────────────────────────────────────────
export function SearchInboxDialog({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const runSearch = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const r = await m365SearchMailbox({ q, limit: 50 });
      setResults(r.emails || []);
    } catch (e) {
      toast.error("Search failed: " + errorDetailMessage(e, e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Search className="w-4 h-4" /> Search shared inbox</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
            placeholder="Search by sender, subject, body text…"
            className="flex-1"
            autoFocus
            data-testid="ageing-email-search-input"
          />
          <Button onClick={runSearch} disabled={loading || !q.trim()} data-testid="ageing-email-search-run">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
            Search
          </Button>
        </div>
        <div className="border rounded max-h-[500px] overflow-y-auto">
          {results.length === 0 ? (
            <div className="text-xs text-muted-foreground italic py-6 text-center">
              {loading ? "Searching…" : "Type a query and press Enter."}
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-muted-foreground border-b sticky top-0">
                <tr>
                  <th className="text-left px-3 py-1.5">When</th>
                  <th className="text-left px-3 py-1.5">Folder</th>
                  <th className="text-left px-3 py-1.5">From</th>
                  <th className="text-left px-3 py-1.5">Subject</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {results.map(e => (
                  <tr key={e.id} className="border-b">
                    <td className="px-3 py-1.5 font-mono text-[11px] text-muted-foreground whitespace-nowrap">{e.receivedDateTime ? new Date(e.receivedDateTime).toLocaleString("en-GB") : "—"}</td>
                    <td className="px-3 py-1.5">
                      <Badge variant="outline" className="text-[10px]">{e.folder_name || "—"}</Badge>
                    </td>
                    <td className="px-3 py-1.5 truncate max-w-[160px]">{(e.from?.emailAddress?.address) || "—"}</td>
                    <td className="px-3 py-1.5 truncate max-w-[300px]">{e.subject || "(no subject)"}</td>
                    <td className="px-3 py-1.5 text-right">
                      {e.webLink && <a href={e.webLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-[10px]">Open ↗</a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── P2P (Promise to Pay) inline panel ──────────────────────────────────
// Lives inside the case drawer alongside notes. Each P2P shows status,
// amount, date, and quick mark-kept / mark-broken actions. Adding a new
// P2P also pushes a synthetic note into the activity timeline.

const P2P_STATUS_META = {
  pending:   { tone: "bg-amber-100 text-amber-900 border-amber-300",       icon: CalendarClock,   label: "Pending" },
  kept:      { tone: "bg-emerald-100 text-emerald-900 border-emerald-300", icon: CheckCircle2,    label: "Kept" },
  broken:    { tone: "bg-red-100 text-red-900 border-red-300",             icon: AlertOctagon,    label: "Broken" },
  cancelled: { tone: "bg-slate-100 text-slate-700 border-slate-300",       icon: Ban,             label: "Cancelled" },
};

const fmtGBP_p2p = (v) => fmtMoney(v || 0, 0);

function P2PPanel({ caseRow, onChanged }) {
  const [showAdd, setShowAdd] = useState(false);
  const [date,    setDate]    = useState("");
  const [amount,  setAmount]  = useState("");
  const [method,  setMethod]  = useState("bank_transfer");
  const [note,    setNote]    = useState("");
  const [busy,    setBusy]    = useState(false);

  const p2ps = [...(caseRow.p2ps || [])].sort((a, b) => (a.promised_at || "").localeCompare(b.promised_at || ""));
  const today = new Date().toISOString().slice(0, 10);

  const reset = () => { setDate(""); setAmount(""); setMethod("bank_transfer"); setNote(""); setShowAdd(false); };

  const add = async () => {
    if (!date || !amount) { toast.error("Date and amount required"); return; }
    try {
      setBusy(true);
      await createAgeingP2P(caseRow.case_id, {
        promised_at:     date,
        promised_amount: parseFloat(amount),
        method,
        notes:           note,
        created_by:      "Ops",
      });
      toast.success("Promise to Pay added");
      reset();
      onChanged();
    } catch (e) {
      toast.error("Failed: " + errorDetailMessage(e, e.message));
    } finally { setBusy(false); }
  };

  const setStatus = async (p, newStatus) => {
    try {
      const patch = { status: newStatus };
      if (newStatus === "kept") patch.kept_amount = p.promised_amount;
      await updateAgeingP2P(caseRow.case_id, p.id, patch);
      toast.success(`P2P marked '${newStatus}'`);
      onChanged();
    } catch (e) {
      toast.error("Failed: " + errorDetailMessage(e, e.message));
    }
  };

  const remove = async (p) => {
    if (!window.confirm("Delete this P2P?")) return;
    try {
      await deleteAgeingP2P(caseRow.case_id, p.id);
      onChanged();
    } catch (e) {
      toast.error("Failed: " + errorDetailMessage(e, e.message));
    }
  };

  return (
    <div className="space-y-2.5" data-testid="ageing-p2p-panel">
      {!showAdd ? (
        <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => { setShowAdd(true); setDate(today); }} data-testid="ageing-p2p-add-btn">
          <CalendarClock className="w-3.5 h-3.5 mr-1.5" /> Schedule a Promise to Pay
        </Button>
      ) : (
        <div className="border rounded-md p-3 bg-amber-50/30 space-y-2" data-testid="ageing-p2p-add-form">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase">Promised date</Label>
              <Input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} className="h-8 text-xs" data-testid="ageing-p2p-date" />
            </div>
            <div>
              <Label className="text-[10px] uppercase">Amount ({cur()})</Label>
              <Input type="number" inputMode="decimal" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="2500" className="h-8 text-xs" data-testid="ageing-p2p-amount" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-8 text-xs" data-testid="ageing-p2p-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                <SelectItem value="direct_debit">Direct debit</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional context — e.g. &apos;Customer phoned, splitting into 3 weekly payments&apos;" className="text-xs" data-testid="ageing-p2p-note" />
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={reset} className="text-xs">Cancel</Button>
            <Button size="sm" onClick={add} disabled={!date || !amount || busy} className="text-xs flex-1" data-testid="ageing-p2p-save-btn">
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Send className="w-3 h-3 mr-1.5" />} Save P2P
            </Button>
          </div>
        </div>
      )}

      {p2ps.length === 0 ? (
        <div className="text-center py-3 text-[11px] text-muted-foreground italic">No promises to pay logged yet.</div>
      ) : (
        <ul className="space-y-1.5" data-testid="ageing-p2p-list">
          {p2ps.map((p) => {
            const status = p.status || "pending";
            const meta = P2P_STATUS_META[status];
            const Icon = meta.icon;
            const overdue = status === "pending" && p.promised_at < today;
            return (
              <li key={p.id} className={`border rounded-md p-2 text-xs ${meta.tone}`} data-testid={`ageing-p2p-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <Icon className="w-3 h-3 shrink-0" />
                      <span className="font-semibold">{fmtGBP_p2p(p.promised_amount)}</span>
                      <span className="text-[10px] opacity-80">on {ukDate(p.promised_at)}</span>
                      {overdue && <Badge className="bg-red-600 text-white text-[9px] px-1.5 h-4">overdue</Badge>}
                    </div>
                    <div className="text-[10px] opacity-70 mt-0.5">
                      via {p.method?.replace("_", " ")} · {meta.label}
                      {p.kept_amount != null && status === "kept" && <> · received {fmtGBP_p2p(p.kept_amount)}</>}
                    </div>
                    {p.notes && <div className="text-[11px] mt-1 italic opacity-90">&ldquo;{p.notes}&rdquo;</div>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {status === "pending" && (
                      <>
                        <Button size="sm" variant="outline" className="h-6 text-[10px] px-1.5 bg-white" onClick={() => setStatus(p, "kept")} data-testid={`ageing-p2p-${p.id}-kept`}>Kept</Button>
                        <Button size="sm" variant="outline" className="h-6 text-[10px] px-1.5 bg-white text-red-700" onClick={() => setStatus(p, "broken")} data-testid={`ageing-p2p-${p.id}-broken`}>Broken</Button>
                      </>
                    )}
                    <button onClick={() => remove(p)} className="opacity-50 hover:opacity-100 hover:text-red-700" title="Delete">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}



// ─── Contactability traffic-light dot ─────────────────────────────────────
// 🟢 easy · 🟠 slow · 🔴 hard · ⚪ unknown (insufficient data).
// Tooltip shows the response rate + attempt count over the 90d window.
const CONTACTABILITY_META = {
  easy:    { color: "bg-emerald-500", border: "border-emerald-600", label: "Easy" },
  slow:    { color: "bg-amber-500",   border: "border-amber-600",   label: "Slow" },
  hard:    { color: "bg-rose-500",    border: "border-rose-600",    label: "Hard" },
  unknown: { color: "bg-slate-200",   border: "border-slate-300",   label: "Unknown" },
};

function ContactabilityDot({ dot }) {
  if (!dot) return <span className="inline-block w-2 h-2 rounded-full bg-slate-200 border border-slate-300" title="Contactability — loading…" />;
  const meta = CONTACTABILITY_META[dot.status] || CONTACTABILITY_META.unknown;
  const rate = dot.response_rate != null ? `${Math.round(dot.response_rate * 100)}%` : "—";
  const tooltip = (
    `Contactability: ${meta.label}\n` +
    `${dot.attempts || 0} outbound attempt${dot.attempts === 1 ? "" : "s"} in last 90 days\n` +
    `Response rate: ${rate}` +
    (dot.status === "unknown" ? "\n(insufficient data — needs ≥5 attempts)" : "")
  );
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${meta.color} border ${meta.border}`}
      title={tooltip}
      aria-label={`Contactability ${meta.label}, ${dot.attempts || 0} outbound attempts, response rate ${rate}`}
      role="img"
      data-testid={`contactability-dot-${dot.status}`}
    />
  );
}
